/**
 * The finished cycles, as a state a screen can render.
 *
 * The same shape as the overview's and the ranking's hooks, for the same
 * reason: a page that had to work out what a combination of booleans means
 * would eventually work it out differently from the next page.
 *
 * Not polled. A history changes when a cycle closes, which is a few times a
 * day, and a page that re-fetched every minute would spend the account's
 * database on re-reading rows that had not moved. It refreshes when the tab is
 * returned to, which is when a person is actually looking.
 */
import { useQuery } from "@tanstack/react-query";

import { readHistory } from "@/shared/api";
import type { Column, HistoryResponse, Order } from "@/shared/api";
import { statusOf } from "@/shared/http/errors";

/** How many cycles one page carries. The server refuses more than 200. */
export const PAGE = 50;

/** Which column orders the record, and which way. Taken from the generated
 *  request type, so a column the server stops accepting stops compiling. */
export type Sort = {
  readonly column: Column;
  readonly order: Order;
};

export const NEWEST_FIRST: Sort = { column: "closed_at", order: "desc" };

export type Range = {
  readonly since: string;
  readonly until: string;
  /** How the owner named it, when they picked it by name rather than by date. */
  readonly label?: string;
};

export type HistoryState =
  | { readonly status: "loading" }
  | { readonly status: "forbidden" }
  | { readonly status: "unreadable" }
  | { readonly status: "ready"; readonly history: HistoryResponse };

export function historyKey(
  range: Range | null,
  offset: number,
  sort: Sort,
  symbol: string | null,
  strategyVersion: string | null,
): readonly unknown[] {
  return [
    "history",
    range?.since ?? null,
    range?.until ?? null,
    offset,
    sort.column,
    sort.order,
    symbol,
    strategyVersion,
  ];
}

export function useHistory(
  range: Range | null,
  offset: number = 0,
  sort: Sort = NEWEST_FIRST,
  symbol: string | null = null,
  /**
   * One strategy, or every strategy. Narrowed by the server rather than by
   * this page: a page carries fifty rows of whatever closed most recently, so
   * a strategy filtered out here would be missing from the record rather than
   * absent from it — and the count beside the rows would describe a different
   * set from the rows.
   */
  strategyVersion: string | null = null,
): HistoryState {
  const query = useQuery({
    queryKey: historyKey(range, offset, sort, symbol, strategyVersion),
    queryFn: async (): Promise<HistoryResponse> => {
      const { data, error, response } = await readHistory({
        query: {
          limit: PAGE,
          offset,
          sort: sort.column,
          order: sort.order,
          ...(symbol === null ? {} : { symbol }),
          ...(strategyVersion === null
            ? {}
            : { strategy_version: strategyVersion }),
          ...(range === null ? {} : { since: range.since, until: range.until }),
        },
      });
      if (error !== undefined || data === undefined) {
        throw Object.assign(new Error("history unreadable"), {
          status: response?.status,
        });
      }
      return data;
    },
    retry: false,
    enabled: range !== null,
  });

  if (query.isPending) return { status: "loading" };
  if (query.isError) {
    return statusOf(query.error) === 403
      ? { status: "forbidden" }
      : { status: "unreadable" };
  }
  return { status: "ready", history: query.data };
}
